import { router } from 'expo-router';
import { memo } from 'react';
import { Pressable, View } from 'react-native';

import { XStack, tokens } from '@fhs/ui';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { IAppliedRewards } from '@fhs-legacy/frontend/src/state/loyalty/hooks/types';
import { useGetAvailableRewards } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-get-available-rewards';
import { useOffersContext } from '@fhs-legacy/frontend/src/state/offers';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { EDIT_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';
import { ICartEntry } from '@rbi-ctg/menu';

import EditQuantityToggle from './edit-quantity-toggle';
import ItemProfile from './item-profile';
import ItemPrice from './item-profile/item-price';
import RewardPointsBar from './reward-points-bar';

type Props = {
  item: ICartEntry;
  redeemableItemsSet: Set<unknown>;
};

function CartItem({ item, redeemableItemsSet }: Props) {
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();

  const appliedLoyaltyRewards: IAppliedRewards = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewards
  );

  // TODO: WAIT FOR REWARDS NOT HAVING TO RELY ON BOTH
  // check if the reward is being applied on this specific item, used in price and showing points bar version
  const isRewardApplied =
    redeemableItemsSet.has(item.cartId) || appliedLoyaltyRewards[item.cartId]?.timesApplied > 0;

  // Check if any reward is applied to hide other reward bars until multiple rewards are allowed
  const hasAnyRewardApplied = Object.values(appliedLoyaltyRewards).some(
    reward => reward?.timesApplied > 0
  );

  const loyaltyRewardAvailable = getAvailableRewardFromCartEntry(item);
  const shouldShowRewardBar = loyaltyRewardAvailable && (!hasAnyRewardApplied || isRewardApplied);

  const { isSelectedOfferCartEntry } = useOffersContext();
  const { cartEntries, setCartIdEditing } = useOrderContext();

  const handleItemEdit = () => {
    /* Updates the cartIdEditing to the new entry only if it exists
       cartIdEditing is used in fresh/ -> useEditFromCartState */
    if (
      isSelectedOfferCartEntry(item) ||
      cartEntries.find(({ cartId }) => cartId === item.cartId)
    ) {
      setCartIdEditing(item.cartId);
    }

    router.dismissTo({
      pathname: item.url,
      params: {
        isEdit: 'true',
        isFromCart: 'true',
      },
    });
  };

  const accessibilityLabel = `${item.name}, quantity ${item.quantity}, ${
    isRewardApplied ? 'reward applied' : 'no reward applied'
  }`;

  return (
    <Pressable
      testID={EDIT_BUTTON}
      onPress={handleItemEdit}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
    >
      <View
        style={{
          padding: 16,
          borderWidth: 1,
          borderRadius: 8,
          borderColor: tokens.colors.$blackOpacity04,
        }}
        accessibilityRole="none"
        accessibilityLabel={`Cart item: ${item.name}`}
      >
        <XStack style={{ gap: 16 }}>
          <ItemProfile item={item} />
          <View style={{ gap: 12 }}>
            <ItemPrice item={item} isRewardApplied={isRewardApplied} />
            <EditQuantityToggle item={item} />
          </View>
        </XStack>
        {shouldShowRewardBar && (
          <RewardPointsBar
            cartId={item.cartId}
            isRewardApplied={isRewardApplied}
            loyaltyRewardAvailable={loyaltyRewardAvailable}
          />
        )}
      </View>
    </Pressable>
  );
}

export default memo(CartItem);
